﻿import React, { useState, useEffect } from 'react';
import { SpectralGraphData } from '../models/spectral-graph-data';
import { FtirAnalysisType, } from '../models/enums';
import { CrudFunctions } from '../utilities/crud-functions';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import { ImageSize } from '../models/image-size';

import Chart, {
    ValueAxis,
    ArgumentAxis,
    Grid,
    Series,
    SeriesTemplate,
    Pane,
    Label,
    Point,
    Legend,
    Margin,
    Border,
    Export,
    WholeRange,
    VisualRange,
    CommonSeriesSettings
} from 'devextreme-react/chart';

import { Title } from 'devextreme-react/circular-gauge';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { NumberBox } from 'devextreme-react/number-box';
import { Button } from 'devextreme-react/button';

export interface Props {
    isReadOnly: boolean;
    benchSheetId: string;
    ftirAnalysisType: FtirAnalysisType;
    spectralImageName?: string;
    spectralGraphHeight?: number;
    updateKey?: number;
    onClose?: () => void;
}

export default function SpectralGraph(props: Props) {
    const [graphData, setGraphData] =
        useState<SpectralGraphData>();

    const [spectralGraphHeight, setSpectralGraphHeight] =
        useState<number>();

    const [spectralGraphWidth, setSpectralGraphWidth] =
        useState<number>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataChanged, setDataChanged] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    //is this needed to have all the props? can just use []??
    useEffect(() => {
        getAllData();
    }, [props.ftirAnalysisType, props.benchSheetId, props.updateKey]);

    /*
    const isUsp = graphData && graphData.points.some(d => d.ftirStandardTypeId === FtirStandardType.USP);
    const isBp = graphData && graphData.points.some(d => d.ftirStandardTypeId === FtirStandardType.BP);
    const isEp = graphData && graphData.points.some(d => d.ftirStandardTypeId === FtirStandardType.EP);
    */

    //temp
    //const isAurum = graphData && graphData.points.some(d => d.ftirStandardTypeId === 1);
    const isAurumPrev = graphData && graphData.points.some(d => d.ftirStandardTypeId === 2);
    const isUsp = graphData && graphData.points.some(d => d.ftirStandardTypeId === 3); // FtirStandardType.USP);
    const isEp = graphData && graphData.points.some(d => d.ftirStandardTypeId === 4); // FtirStandardType.EP);


    const totalPanes = graphData ? (1 + (isAurumPrev ? 1 : 0) +
        (isUsp ? 1 : 0) + (isEp ? 1 : 0)) : 0;


    /* ORIG
    const totalPanes = graphData ? (1 + (isUsp ? 1 : 0) +
        (isBp ? 1 : 0) + (isEp ? 1 : 0)) : 0;
    */

    const paneHeight = (spectralGraphHeight ? spectralGraphHeight : 300) / totalPanes;

    const showLoadIndicator = !dataLoaded;
    const showNoData = dataLoaded && (!graphData || graphData && graphData.points && graphData.points.length === 0);
    const showGraph = dataLoaded && graphData && graphData.points && graphData.points.length > 0;

    const width = 1000;

    return (
        <div>
            <div className="pw-comp-row pw-margin-top-05">
                <div className="pw-form-unit pw-comp-15">
                    <div className="pw-form-unit-label">Graph Height</div>
                    <NumberBox id="spectralGraphHeightValueId" className="textbox" readOnly={props.isReadOnly}
                        onValueChanged={onSpectralGraphHeightValueChanged}
                        value={spectralGraphHeight ? spectralGraphHeight : undefined} >
                    </NumberBox>
                </div>

                <div className="pw-form-unit pw-comp-15">
                    <div className="pw-form-unit-label">Graph Width</div>
                    <NumberBox id="spectralGraphWidthValueId" className="textbox" readOnly={props.isReadOnly}
                        onValueChanged={onSpectralGraphWidthValueChanged}
                        value={spectralGraphWidth ? spectralGraphWidth : undefined} >
                    </NumberBox>
                </div>
                <div className="title-bar-spacer" />
                <div className="save-toolbar pw-margin-right-05">
                    <Button id="saveButton" text="Save" icon="save" type="success"
                        className="pw-button"
                        hint="save changes"
                        height={'1.8rem'}
                        onClick={onSaveClick} />

                    <Button id="closeButton" text="Close" icon="close" type="normal"
                        className="pw-button"
                        hint="close dialog"
                        height={'1.8rem'}
                        onClick={onCloseClick} />
                </div>
            </div>

            {showLoadIndicator &&
                <LoadIndicator id="small-indicator" height={40} width={40} visible={showLoadIndicator} />
            }
            {showNoData &&
                <div className="missing-data-container">
                    <div className="missing-data-content">NO GRAPH DATA</div>
                </div>
            }
            {showGraph &&
                <div>
                    {/* <div style={{ width: '100%', margin: "auto" }}> */}
                    <Chart dataSource={graphData && graphData.points ?
                        graphData.points : []}
                        width={spectralGraphWidth}
                        style={{ margin: "auto" }}>

                        {/* width={width} */}

                        <Title text={props.spectralImageName}>
                            <Margin top={5} bottom={40} />
                        </Title>

                        <Border width={0.1} color="red" />

                        <Series pane="topPane"
                            argumentField="wn"
                            valueField="amp"
                            color="#cc0000"
                            type="spline">
                            <Point visible={false} />
                        </Series>

                        <SeriesTemplate nameField="std"
                            customizeSeries={customSeriesFunc} />

                        <ValueAxis pane="topPane"
                            grid={{ opacity: 0.2 }}
                            endOnTick={true}
                            tickInterval={20}
                            valueType="numeric">
                            <Title text="Aurum %T" />
                            <WholeRange startValue={0} endValue={140} />
                            <VisualRange startValue={0} endValue={140} />
                        </ValueAxis>

                        <Pane name="topPane" height={paneHeight} />

                        {isAurumPrev &&
                            <Pane name="aurumPrevPane" height={paneHeight} />
                        }
                        {isUsp &&
                            <Pane name="uspPane" height={paneHeight} />
                        }
                        {isEp &&
                            <Pane name="epPane" height={paneHeight} />
                        }

                        {isAurumPrev &&
                            <ValueAxis pane="aurumPrevPane"
                                grid={{ opacity: 0.2 }}
                                endOnTick={true}
                                tickInterval={20}
                                valueType="numeric">
                                <Title text="Aurum(Prev) %T" />
                                <WholeRange startValue={0} endValue={140} />
                                <VisualRange startValue={0} endValue={140} />
                            </ValueAxis>

                        }
                        {isUsp &&
                            <ValueAxis pane="uspPane"
                                grid={{ opacity: 0.2 }}
                                endOnTick={true}
                                tickInterval={20}
                                valueType="numeric">
                                <Title text="USP %T" />
                                <WholeRange startValue={0} endValue={140} />
                                <VisualRange startValue={0} endValue={140} />
                            </ValueAxis>

                        }
                        {isEp &&
                            <ValueAxis pane="epPane"
                                grid={{ opacity: 0.2 }}
                                endOnTick={true}
                                tickInterval={20}
                                valueType="numeric">
                                <Title text="EP %T" />
                                <WholeRange startValue={0} endValue={140} />
                                <VisualRange startValue={0} endValue={140} />
                            </ValueAxis>
                        }

                        <ArgumentAxis type="continuous" tickInterval={500} inverted={true}
                            minValueMargin={0.01} maxValueMargin={0.01}>
                            <Grid visible={false} opacity={0.5} />

                            <VisualRange startValue={600} endValue={4020} />
                            <WholeRange startValue={-20} endValue={4020} />
                            <Title text="Wavenumbers (cm⁻¹)" />
                            <Label format={'#'} />
                        </ArgumentAxis>

                        {/*
                         <VisualRange startValue={600} endValue={4020} />
                            <WholeRange startValue={-20} endValue={4020} />
                           */}


                        <CommonSeriesSettings
                            valueField="amp"
                            type="spline"
                            argumentField="wn"
                            width={1}>
                            <Point visible={false} />
                        </CommonSeriesSettings>

                        <Legend visible={false} />
                        <Export enabled={true} />
                    </Chart>
                </div>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    async function onSaveClick() {
        if (!dataChanged)
            return;

        const imageSize: ImageSize = {
            height: spectralGraphHeight,
            width: spectralGraphWidth
        }

        await updateSpectralImageSize(imageSize)
            .then(() => {

            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Spectral Image Size");
            });
    }

    function onCloseClick() {
        if (props.onClose)
            props.onClose();
    }

    function onSpectralGraphHeightValueChanged(e: any) {
        if (!dataLoaded)
            return;

        setDataChanged(true);
        setSpectralGraphHeight(e.value);
    }

    function onSpectralGraphWidthValueChanged(e: any) {
        if (!dataLoaded)
            return;

        setDataChanged(true);
        setSpectralGraphWidth(e.value);
    }

    function customSeriesFunc(std: any) {
        if (std === 1)
            return { pane: "topPane", color: "red" }
        else if (std === 2)
            return { pane: "aurumPrevPane", color: "blueviolet" }
        else if (std === 3)
            return { pane: "uspPane", color: "limegreen" }
        else if (std === 4)
            return { pane: "epPane", color: "deepskyblue" }
    }

    /*
    function customSeriesFunc(std: any) {
        if (std === ManufacturerTypes.MSGS)
            return { pane: "topPane", color: "red" }
        else if (std === ManufacturerTypes.USP)
            return { pane: "uspPane", color: "blueviolet" }
        else if (std === ManufacturerTypes.EP)
            return { pane: "epPane", color: "limegreen" }
        else if (std === ManufacturerTypes.BP)
            return { pane: "bpPane", color: "deepskyblue" }
    }
    */

    async function getSpectralGraphData() {
        await CrudFunctions.getData(`benchsheets/spectralgraphfiledata/${props.benchSheetId}/${props.ftirAnalysisType}`)
            .then((data) => {
                console.log("SPECTRAL GRAPH DATA:");
                //console.log(data);

                let s1 = data.points.filter(d => d.ftirStandardTypeId === 1);
                let s3 = data.points.filter(d => d.ftirStandardTypeId === 3);

                console.log(s1);
                console.log(s3);



                setGraphData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Spectral Graph Data");
            });
    }

    async function getSpectralImageSize() {
        await CrudFunctions.getData(`benchsheets/spectralimagesize/${props.benchSheetId}/${props.ftirAnalysisType}`)
            .then((data) => {
                //console.log("SPECTRAL GRAPH DATA:");
                //console.log(data);

                const imageSize = data as ImageSize;

                if (imageSize) {
                    setSpectralGraphHeight(imageSize.height);
                    setSpectralGraphWidth(imageSize.width);
                }
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Spectral Image Size");
            });
    }

    async function updateSpectralImageSize(imageSize: ImageSize) {
        return await CrudFunctions.putData(`benchsheets/spectralimagesize/${props.benchSheetId}/${props.ftirAnalysisType}`, imageSize)
    }

    async function getAllData() {
        if (props.benchSheetId && props.ftirAnalysisType) {
            await Promise.all([
                getSpectralGraphData(),
                getSpectralImageSize()
            ])
                .then(() => setDataLoaded(true));
        }
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
